import { Button, Container, Grid, RootRef, Typography, useMediaQuery } from '@material-ui/core/';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { Skeleton } from '@material-ui/lab';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import AppToolBar from '~/components/AppToolBar';
import Cities from '~/components/Cities';
import DeliveryAddressInput from '~/components/DeliveryAddressInput';
import DiscoverOurPlatform from '~/components/DiscoverOurPlatform';
import ImageBox from '~/components/ImageBox';
import Page from '~/components/Page';
import ServiceNav from '~/components/Services';
import Callout, { CalloutText } from '~/components/ui-kit/Callout/Callout';
import Subtitle from '~/components/ui-kit/Subtitle/Subtitle';
import Video from '~/components/Video';
import Youtube from '~/components/Youtube';
import { useAppConfig } from '~/hooks/useAppConfig';
import { useGoogleMaps } from '~/hooks/useGoogleMaps';
import { getUserStore } from '~/store/legacy/selector/userSelectors';

const DownloadApp = dynamic(() => import('~/components/DownloadApp'), { ssr: false });

const useStyles = makeStyles((theme) => ({
  landingHero: {
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      height: 500,
    },
    height: 500,
  },
  downloadContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: '60px 0',
    },
  },
  bold: {
    fontFamily: 'sofia-bold, sans-serif',
  },
  citiesImage: {
    display: 'flex',
    justifyContent: 'center',
  },
  clipBackground: {
    [theme.breakpoints.up('xl')]: {
      clipPath: 'circle(5000px at 50% -4515px)',
      '-webkit-clip-path': 'circle(5000px at 50% -4515px)',
    },
    position: 'absolute',
    backgroundColor: '#e00051',
    clipPath: 'circle(3500px at 50% -3015px)',
    '-webkit-clip-path': 'circle(3500px at 50% -3015px)',
    width: '100%',
    height: '100%',
  },
  clip: {
    [theme.breakpoints.up('xl')]: {
      clipPath: 'circle(3260px at 50% -2760px)',
      '-webkit-clip-path': 'circle(3260px at 50% -2760px)',
    },
    zIndex: 1,
    top: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    clipPath: 'circle(2260px at 50% -1767px)',
    '-webkit-clip-path': 'circle(2260px at 50% -1767px)',
    overflow: 'hidden',
  },
  landingHeroLoading: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  landingOverlay: {
    zIndex: 2,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: '100%',
  },
  landingOverlayTrans: {
    zIndex: 2,
  },
  subtitleText: {
    color: '#9f9f9f',
  },
  featuredImageContainer: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  featuredDishImageContainer: {
    position: 'relative',
    backgroundColor: '#2a2a2d',
    textAlign: 'right',
    height: 550,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexBasis: 'auto',
    },
  },
  featuredImageTextContainer: {
    padding: '40px 0',
    height: 550,
    backgroundColor: '#f7f7f7',
  },
  featuredImageText: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
    },
    maxWidth: 430,
  },
  restaurantBundlingContainer: {
    textAlign: 'center',
    minHeight: 550,
    padding: '60px 0',
    backgroundColor: '#f9eeeb',
  },
  serviceContainer: {
    minHeight: 450,
    maxWidth: 1024,
  },
  serviceIcon: {
    width: '12em',
    height: '12em',
    marginBottom: 20,
  },
  landingOverlayLinks: {
    animation: 'feature-text-anim .75s ease-in-out',
  },
  '@keyframes feature-text-anim': {
    '0%': {
      opacity: 0,
      transform: 'translate3d(0,11px,0)',
    },
    '75%': {
      opacity: 0,
      transform: 'translate3d(0,10px,0)',
    },
    '100%': {
      opacity: 1,
      transform: 'translate3d(0,0,0)',
    },
  },
  limitedWidth: {
    maxWidth: 1024,
  },
  featureText: {
    color: 'white',
    marginBottom: 35,
    [theme.breakpoints.down('sm')]: {
      fontSize: 32,
    },
  },
  featureButton: {
    color: 'white',
    borderColor: 'white',
    borderWidth: 2,
    fontSize: 16,
    letterSpacing: 1,
  },
  inputContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
    backgroundColor: '#f2f2f2',
    padding: '30px 15px',
  },
  content: {
    [theme.breakpoints.up('sm')]: {
      width: '65%',
      minWidth: 700,
      margin: 'auto',
    },
  },
  textMe: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 20,
      width: '100%',
    },
  },
  phone: {
    width: '120%',
    marginLeft: '-10%',
  },
  plusContainer: {
    minHeight: 500,
    backgroundColor: '#fcfbf5',
  },
  readyToOrder: {
    minHeight: 360,
    backgroundColor: theme.palette.primary.main,
  },
}));

const FeaturedImage = (props) => {
  const classes = useStyles();
  const [youtubeReady, setYoutbubeReady] = useState(false);
  const { width } = props;
  const handleYoutubeStateChange = (status) => {
    if (status === 1) {
      setYoutbubeReady(true);
    }
  };
  return (
    <div className={classes.landingHeroLoading}>
      <div className={classes.clipBackground}></div>
      <div className={classNames(classes.clip)} >
        {isWidthDown('sm', width) ? (
          <ImageBox
            src='/homepage/landing-hero-mobile-bg.jpg'
            alt='Picture of a table laden with Chinese food dishes'
            fit='cover'
          />
        ) : (
          <ImageBox
            src='/homepage/web_banner.jpg'
            alt='Picture of a table laden with Chinese food dishes'
            fit='cover'
          />
          // <Youtube
          //   onPlayerStateChange={handleYoutubeStateChange}
          //   id='landing-introduction'
          //   videoId='NSLzgSnPabM'
          // />
        )}
        {/* <div
          style={{
            top: 0,
            width: '100%',
            height: '100%',
            position: 'absolute',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}></div> */}
      </div>
      <Grid
        container
        justify='center'
        className={classNames(classes.landingOverlay, {
          [classes.landingOverlayTrans]: youtubeReady,
        })}>
        <Grid
          container
          justify='center'
          alignItems='center'
          className={classes.landingOverlayLinks}>
          <Grid item xs={12}>
            <Typography
              variant='h3'
              align='center'
              paragraph
              className={classNames(classes.featureText, classes.bold)}>
              Best Asian Food, Delivered
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={11} sm={9} md={7} lg={6} style={{ marginTop: -100 }}>
          <DeliveryAddressInput callToAction='Order Now' />
        </Grid>
      </Grid>
    </div>
  );
};

const ResponsiveImage = withWidth()(FeaturedImage);

const AboutSection = () => {
  const classes = useStyles();
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <section>
      <Grid container className={classes.featuredImageContainer}>
        <Grid item md={6} xs={12} className={classes.featuredDishImageContainer}>
          <ImageBox
            src='/homepage/landing-feature-dish.png'
            alt='Picture of several Chinese Dishes'
            fit='cover'
            style={{ position: 'absolute', height: 550, right: 0 }}
          />
        </Grid>
        <Grid
          item
          container
          alignItems='center'
          justify={small ? 'center' : 'flex-start'}
          md={6}
          xs={12}
          className={classes.featuredImageTextContainer}>
          <div style={{ marginLeft: small ? 0 : 100 }} className={classes.featuredImageText}>
            <Subtitle hLevel={4} color='yellow'>
              Every dish is handpicked with a reason
            </Subtitle>
            <Typography
              variant='subtitle1'
              className={classes.subtitleText}
              paragraph
              style={{ marginTop: 20 }}>
              We travel far and wide to find the best restaurants that serve food that emanates love
              and care. Every meal offered on Chowbus is handpicked to ensure quality!
            </Typography>
            <Grid item>
              <Link href='/about'>
                <Button
                  style={{
                    width: '100%',
                    height: 56,
                    borderRadius: 28,
                    backgroundColor: '#292929',
                  }}
                  variant='contained'
                  color='primary'>
                  ABOUT
                </Button>
              </Link>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </section>
  );
};

const RestaurantBundling = () => {
  const classes = useStyles();
  const router = useRouter();
  const googleMaps = useGoogleMaps();
  const { address } = useSelector(getUserStore);

  const goToRestaurant = () => {
    if (address) {
      router.push(`/delivery/[address]`, `/delivery/${address.address_1}`);
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const place = await googleMaps.getGeoLocation({ location: { lat, lng } });
            const { formatted_address } = place;
            if (formatted_address) {
              router.push(
                `/restaurants/around/[address]`,
                `/restaurants/around/${encodeURI(formatted_address)}`
              );
            }
          },
          () => {}
        );
      }
    }
  };

  return (
    <section>
      <div className={classes.restaurantBundlingContainer}>
        <Container maxWidth='md'>
          <Grid container justify='center'>
            <Typography paragraph variant='h4' style={{ fontFamily: 'sofia-bold' }}>
              Restaurants Bundling
            </Typography>
            <Typography paragraph variant='body1' style={{ color: '#9b9b9b' }}>
              Customers can order from different restaurants in a single order with no added fees,
              letting them sample a wider variety of dishes in one meal.
            </Typography>
            <Grid item>
              <Button
                variant='contained'
                onClick={goToRestaurant}
                style={{ width: 256, borderRadius: 30, height: 56, backgroundColor: '#fdd129' }}>
                <Typography variant='subtitle1'>FIND RESTAURANTS</Typography>
              </Button>
            </Grid>
          </Grid>
          <div style={{ marginTop: 40, position: 'relative' }}>
            <ImageBox
              src='/homepage/landing-home-bundle.png'
              fit='width'
              alt='Illustration of two meals being bundled together with a plus symbol'
            />
          </div>
        </Container>
      </div>
    </section>
  );
};

const ServiceSection = () => {
  const classes = useStyles();
  return (
    <section>
      <Grid container justify='center'>
        <Grid container direction='column' style={{ padding: '80px 0' }}>
          <Typography variant='h4' className={classes.bold} paragraph align='center'>
            Our Services
          </Typography>
          <Typography variant='subtitle1' className={classes.subtitleText} paragraph align='center'>
            Find the service* that best fit your Needs.
          </Typography>
          <ServiceNav showDescription textColor='#000' backgroundColor='#f8f8f8' />
        </Grid>
      </Grid>
    </section>
  );
};

const WithInViewVideo = (props) => {
  const { src } = props;
  const [video, setVideo] = useState(null);
  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) {
      video.play();
    }
  }, [inView]);
  return (
    <RootRef rootRef={ref}>
      <Video onRef={(v) => setVideo(v)} src={src} />
    </RootRef>
  );
};

const PlusMemberSection = () => {
  const classes = useStyles();
  const appConfig = useAppConfig();
  return (
    <section>
      <Grid container justify='center' className={classes.plusContainer}>
        <Grid container style={{ padding: '80px 0' }} className={classNames(classes.limitedWidth)}>
          <Grid item container alignItems='center' justify='center' sm={6} xs={12}>
            <div className={classes.featuredImageText}>
              <Subtitle hLevel={4} color='green'>
                Free Delivery and Exclusive Discounts
              </Subtitle>
              <Typography
                variant='subtitle1'
                paragraph
                style={{ marginTop: 20 }}
                className={classes.subtitleText}>
                Chowbus Plus members get free delivery and exclusive discounts. Try it for free,
                cancel anytime (Free delivery on orders over $
                {appConfig.isLoading ? (
                  <Skeleton variant='text' />
                ) : (
                  appConfig.plusMembershipMinimum
                )}
                )
              </Typography>
              <Button
                href='/my-account/plus'
                color='secondary'
                variant='contained'
                style={{ borderRadius: 28, height: 56 }}>
                START 30 DAYS FREE TRIAL
              </Button>
            </div>
          </Grid>
          <Grid item sm={6} xs={12} container alignItems='center' justify='center'>
            <WithInViewVideo src='/homepage/plus-member.mp4' />
          </Grid>
        </Grid>
      </Grid>
    </section>
  );
};

const MapSection = () => {
  const classes = useStyles();
  return (
    <section>
      <Grid
        container
        alignItems='center'
        justify='center'
        style={{ minHeight: 400, margin: '80px 0' }}>
        <Grid item>
          <ImageBox
            src='/homepage/landing-cities-image.png'
            alt='Illustration of several world cities'
            className={classes.citiesImage}
          />
        </Grid>
        <Grid item container justify='center' alignItems='center' sm={4} xs={10}>
          <Callout title='Cities We Serve'>
            <CalloutText component='div'>
              <Cities />
            </CalloutText>
          </Callout>
        </Grid>
      </Grid>
    </section>
  );
};

const ReadyToOrder = () => {
  const classes = useStyles();
  return (
    <section>
      <Grid container justify='center' alignItems='center' className={classes.readyToOrder}>
        <Grid item xs={12}>
          <Typography
            className={classes.bold}
            variant='h3'
            align='center'
            style={{ color: 'white' }}>
            Ready to order?
          </Typography>
        </Grid>
        <Grid item xs={11} sm={9} md={7} lg={6} style={{ marginTop: -100 }}>
          <DeliveryAddressInput callToAction='Order Now' />
        </Grid>
      </Grid>
    </section>
  );
};

const Index = () => {
  const classes = useStyles();
  const appConfig = useAppConfig();

  return (
    <Page name='landing' fullWidth>
      <AppToolBar position='absolute' variant='secondary' />
      <section className={classes.landingHero}>
        <ResponsiveImage />
      </section>
      {/* <HomepageBanner /> */}
      <Grid container justify='center' style={{ minHeight: appConfig.isAlipay ? 0 : 550 }}>
        {appConfig.isLoading ? (
          <Skeleton variant='rect' height={550} width='100%' />
        ) : (
          <DownloadApp />
        )}
      </Grid>
      <AboutSection />
      <RestaurantBundling />
      <DiscoverOurPlatform />
      <ServiceSection />
      <PlusMemberSection />
      <MapSection />
      <ReadyToOrder />
    </Page>
  );
};

export default Index;

export async function getStaticProps() {
  return {
    props: {}, // will be passed to the page component as props
    revalidate: 300, // regenerate every 5 minutes
  };
}
